import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Calendar, Plus, CheckCircle, XCircle, List, CalendarDays } from 'lucide-react';
import LeaveCalendar from '../components/LeaveCalendar';

import API_URL from '../utils/api';

const Leaves = () => {
    const { user, canManage } = useAuth();
    const [leaves, setLeaves] = useState([]);
    const [pendingLeaves, setPendingLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [approvalDialog, setApprovalDialog] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [formData, setFormData] = useState({
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: ''
    });
    const [approvalData, setApprovalData] = useState({
        status: 'approved',
        remarks: ''
    });

    useEffect(() => {
        fetchData();
    }, [canManage]);

    const fetchData = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/leaves`, { withCredentials: true });
            setLeaves(data);
            
            if (canManage) {
                const { data: pending } = await axios.get(`${API_URL}/api/leaves/pending`, { withCredentials: true });
                setPendingLeaves(pending);
            }
        } catch (error) {
            logger.error('Error fetching leaves:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                start_date: new Date(formData.start_date).toISOString(),
                end_date: new Date(formData.end_date).toISOString()
            };

            await axios.post(`${API_URL}/api/leaves`, payload, { withCredentials: true });
            setIsDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error submitting leave request');
        }
    };

    const handleApproval = async () => {
        if (!approvalDialog) return;
        try {
            await axios.put(
                `${API_URL}/api/leaves/${approvalDialog._id}/approve`,
                approvalData,
                { withCredentials: true }
            );
            setApprovalDialog(null);
            setApprovalData({ status: 'approved', remarks: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error processing leave request');
        }
    };

    const resetForm = () => {
        setFormData({
            leave_type: 'annual',
            start_date: '',
            end_date: '',
            reason: ''
        });
    };

    const leaveTypes = [
        { value: 'annual', label: 'Annual Leave' },
        { value: 'sick', label: 'Sick Leave' },
        { value: 'casual', label: 'Casual Leave' },
        { value: 'maternity', label: 'Maternity Leave' },
        { value: 'paternity', label: 'Paternity Leave' },
        { value: 'unpaid', label: 'Unpaid Leave' }
    ];

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-amber-100 text-amber-800 border-amber-200',
            approved: 'bg-green-100 text-green-800 border-green-200',
            rejected: 'bg-red-100 text-red-800 border-red-200'
        };
        return <Badge className={`rounded-full px-3 py-0.5 capitalize ${styles[status] || styles.pending}`}>{status}</Badge>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading leave data...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="leaves-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Leave Management
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {canManage ? 'Manage employee leave requests' : 'Submit and track your leave requests'}
                    </p>
                </div>
                
                <div className="flex gap-2">
                    <div className="flex border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
                        <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className={viewMode === 'list' ? 'rounded-none bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'rounded-none'} onClick={() => setViewMode('list')} data-testid="view-list-btn">
                            <List className="h-4 w-4 mr-1" />List
                        </Button>
                        <Button variant={viewMode === 'calendar' ? 'default' : 'ghost'} size="sm" className={viewMode === 'calendar' ? 'rounded-none bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'rounded-none'} onClick={() => setViewMode('calendar')} data-testid="view-calendar-btn">
                            <CalendarDays className="h-4 w-4 mr-1" />Calendar
                        </Button>
                    </div>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="request-leave-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Request Leave
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                Request Leave
                            </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Submit a leave request — type, dates, and reason.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Leave Type</Label>
                                <Select value={formData.leave_type} onValueChange={(v) => setFormData({...formData, leave_type: v})}>
                                    <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="leave-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-lg">
                                        {leaveTypes.map(type => (
                                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Start Date</Label>
                                    <Input
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="leave-start-date-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">End Date</Label>
                                    <Input
                                        type="date"
                                        value={formData.end_date}
                                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="leave-end-date-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reason</Label>
                                <Textarea
                                    value={formData.reason}
                                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                                    className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                    rows={3}
                                    required
                                    data-testid="leave-reason-input"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-lg" onClick={() => setIsDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="leave-submit-btn">
                                    Submit Request
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {/* Calendar View */}
            {viewMode === 'calendar' && <LeaveCalendar />}

            {/* List View */}
            {viewMode === 'list' && (
            <>
            {/* Pending Approvals (Admin/HR only) */}
            {canManage && pendingLeaves.length > 0 && (
                <Card className="rounded-xl shadow-sm border-slate-200 border-l-4 border-l-amber-500 overflow-hidden">
                    <CardHeader className="border-b border-slate-200">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Calendar className="h-5 w-5 text-amber-500" />
                            Pending Approvals ({pendingLeaves.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-slate-200">
                            {pendingLeaves.map((leave) => (
                                <div key={leave._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                                    <div>
                                        <p className="font-medium text-slate-900">{leave.employee_name}</p>
                                        <p className="text-sm text-slate-500">
                                            {leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)} Leave: {' '}
                                            {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                                        </p>
                                        <p className="text-sm text-slate-600 mt-1">{leave.reason}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm"
                                            onClick={() => {
                                                setApprovalDialog(leave);
                                                setApprovalData({ status: 'approved', remarks: '' });
                                            }}
                                            data-testid={`approve-leave-${leave._id}`}
                                        >
                                            <CheckCircle className="h-4 w-4 mr-1" />
                                            Approve
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-lg border-red-300 text-red-600 hover:bg-red-50"
                                            onClick={() => {
                                                setApprovalDialog(leave);
                                                setApprovalData({ status: 'rejected', remarks: '' });
                                            }}
                                            data-testid={`reject-leave-${leave._id}`}
                                        >
                                            <XCircle className="h-4 w-4 mr-1" />
                                            Reject
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* All Leave Requests */}
            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                <CardHeader className="border-b border-slate-200 bg-slate-50/50">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Calendar className="h-5 w-5 text-[#2563eb]" />
                        {canManage ? 'All Leave Requests' : 'My Leave Requests'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="leaves-table">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    {canManage && <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>}
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Leave Type</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Duration</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Reason</th>
                                    <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaves.map((leave) => (
                                    <tr key={leave._id || `${leave.employee_name}-${leave.start_date}`} className="border-b border-slate-200 hover:bg-slate-50">
                                        {canManage && <td className="p-4 font-medium text-slate-900">{leave.employee_name}</td>}
                                        <td className="p-4 text-slate-700 capitalize">{leave.leave_type.replace('_', ' ')}</td>
                                        <td className="p-4 text-slate-700">
                                            {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                                        </td>
                                        <td className="p-4 text-slate-700 max-w-xs truncate">{leave.reason}</td>
                                        <td className="p-4 text-center">{getStatusBadge(leave.status)}</td>
                                    </tr>
                                ))}
                                {leaves.length === 0 && (
                                    <tr>
                                        <td colSpan={canManage ? 5 : 4} className="p-8 text-center text-slate-500">
                                            No leave requests found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
            </>
            )}

            {/* Approval Dialog */}
            <Dialog open={!!approvalDialog} onOpenChange={() => setApprovalDialog(null)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            {approvalData.status === 'approved' ? 'Approve' : 'Reject'} Leave Request
                        </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Provide the details below to continue.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Remarks (Optional)</Label>
                            <Textarea
                                value={approvalData.remarks}
                                onChange={(e) => setApprovalData({...approvalData, remarks: e.target.value})}
                                className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                rows={3}
                                placeholder="Add any remarks..."
                                data-testid="approval-remarks-input"
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" className="rounded-lg" onClick={() => setApprovalDialog(null)}>
                                Cancel
                            </Button>
                            <Button
                                className={`rounded-lg shadow-sm text-white ${approvalData.status === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
                                onClick={handleApproval}
                                data-testid="confirm-approval-btn"
                            >
                                Confirm {approvalData.status === 'approved' ? 'Approval' : 'Rejection'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Leaves;
